
import img0 from './Images/Subsetting_Config.png';
import img1 from './Images/Encryption_Settings3.png';
import img2 from './Images/Select_Database_Link.png';
  
const content = `# Database Subsetting Feature
For the test data generation use case, when the data volume and size of a Production database are very large, we only need a subset of the data from the Production 
database when generating test data for lower environments, such as Development, Testing, Staging and Training environments. The OnData Encryption Tool provides an 
option to \`Apply Subsetting\` when generating test data. Based on the subsetting configuration at the OnData Portal, the OnData Encryption Tool will extract a 
subset of the data from a Production database, with referential integrity preserved, and apply encryption as well as optional obfuscation, and stored the processed 
data into a separate database, which can be more conveniently used in lower environments.  
  
**Note**: At this time the Database Subsetting feature is only supported for Microsoft SQL Server, Oracle, and DB2 databases.  
  
## 1. Subsetting Configuration at OnData Portal
You may follow the steps below to configure the subsetting options for a database:  
a. Login to the OnData Portal and navigate to \`Services\` -> \`Encryption\`  
b. Select the \`Database Subsetting\` tab from the \`Encryption Dashboard\`  
c. Search and locate the database to be configured and click on the \`Details\` icon on the right side  
d. Set the \`Subset Threshold\`. If the total number of rows in a table is less than the Subset Threshold, by default all rows in that table will be copied over, 
instead of taking a subset of rows from that table  
e. Under the Master Table settings, choose to enable or disable the option of \`Include Standalone Tables\`. In this context, Master tables are "leaf tables" 
that do not have any child tables referencing them with a foreign key. They are at the end of a hierarchical or relational structure in an E-R diagram. They 
typically contain specific data and have the maximum number of rows in a database. A table without any relationship with other tables is considered a standalone table  
f. Set the number of rows to be taken from each Master table. OnData identifies all Master tables based on the database schema. For each Master table, the total 
number of rows and the total number of its upstream tables are displayed. You can click on a Master table on the left side and enter the number of rows to be 
extracted as part of the subsetting process. The Master table will be displayed on the right side  
g. For each Master table, you have the option to further filter the rows to be included in the subset. By clicking on the \`Add Filters\` icon, you can add \`WHERE\` 
and \`ORDER BY\` clauses to filter the rows to be selected. Please note the following when adding filters:  
  * Not all query syntaxes are supported in the \`WHERE\` and \`ORDER BY\` clauses, such as functions. Try to avoid very complex clauses
  * For very large tables, you may need to consider the performance of the filters, and add indexes as needed to improve query performance
  * It is recommended to check the filters with your database tables to figure out how many rows will be extracted using such filters   

h. OnData will automatically figure out all related rows to be taken from all upstream tables, so that referential integrity is fully preserved after the 
subsetting process  
i. Save all changes made to the subsetting configuration  
  
![Subsetting Config](${ img0 })  
  
## 2. Subsetting Prerequisites
To perform database subsetting, the OnData Encryption Tool will read a subset of data records from a Source database based on the subsetting configuration at the OnData 
Portal, perform data encryption and/or data obfuscation, and write the data records to a Target database. the Target database needs to be prepared before running the 
OnData Encryption Tool with the subsetting option.  

### 2.1 Microsoft SQL Server Database Prerequisites
If the Source database is a Microsoft SQL Server database, you will need to follow the steps below to prepare a Target database:
* Export the schema of the Source database. You can use the Microsoft SQL Server Management Studio to do that  
a. Right click on the Source database  
b. Select \`Tasks\` > \`Generate Scripts...\`  
c. On the \`Choose Objects\` page, select the option of \`Script entire database and all database objects\` and generate the database script  

* Edit the generated database script to update the database name with the Target database name, and update the file location for the Target database  
* Run the database script to create the database schema for the Target database 
  
After the Target database is created, first grant the database user the \`db_owner\` role to the Target database, then run the OnData Encryption Tool to extract a subset 
of data records from the Source database based on the subsetting configuration at the OnData Portal, process the extracted data, and store the processed data into 
the Target database.  

**Note**: You may want to review all constraints in the Target database to ensure they will not prevent the encrypted or obfuscated data from being stored. Or you 
may choose to disable all constraints in the Target database before running the OnData Encryption Tool.  
  
### 2.2 Oracle Database Prerequisites
If the Source database is an Oracle database, you need to follow the detailed instructions in \`Section 1. Prerequisites\` and \`Section 2.1 Copy Database Schema\` of 
the documentation [**Migrate and Encrypt Oracle Database**](reference_doc#securedb/ondata_encryption/migrate_and_encrypt_oracle_database) to prepare a Target database and a database link from the Target 
database to the Source database before you run the OnData Encryption Tool with the subsetting option.

**Note**: Some older versions of Oracle databases may still use the \`LONG\` or \`LONG LAW\` data types. The OnData Encryption Tool cannot handle table columns with 
such data types when the subsetting option is enabled. If you have such table columns in your Source database, you may convert the data type from LONG to CLOB, 
and from LONG LAW to BLOB, before you run the OnData Encryption Tool with the subsetting option.
  
### 2.3 DB2 Database Prerequisites
If the Source database is a DB2 database, you will need to follow the steps below to prepare a Target database:
* Configure the user permission to the Source database  
  a. Grant the \`CONNECT\` authority to the user: GRANT CONNECT ON DATABASE TO USER {username};  
  b. Grant the \`SELECT\` privileges on the following catalog views:   
  * SYSCAT.TABLES
  * SYSCAT.COLUMNS
  * SYSCAT.VIEWS
  * SYSCAT.TABCONST
  * SYSCAT.KEYCOLUSE
  * SYSCAT.REFERENCES
  * SYSCAT.TRIGGERS
  * SYSCAT.INDEXES
  * SYSCAT.INDEXCOLUSE
  * SYSCAT.ROUTINES 
  
  c. Grant the \`SELECT\` privileges on all tables to be migrated from Source to Target: GRANT SELECT ON {table_name} TO USER {username};  
  d. Grant the \`SYSMON\` instance authority to the user  
  
* Export the schema of the Source database. You can use the \`db2look\` utility extract the Data Definition Language (DDL) statements. You can find the documentation 
[here](https://www.ibm.com/docs/en/db2/11.5.x?topic=commands-db2look-db2-statistics-ddl-extraction-tool). A sample command is provided below:  

\`\`\`bash
db2look -d {database_name} -e -a -x -l -f -o {database_name}.sql

\`\`\`
* Use the exported database schema to create the Target database  
* Configure the user permission to the Target database  
  a. It is recommended to use the database user who has created the Target database
  b. Grant the \`DBADM\` authority to the database user for creating federation related resources: GRANT DBADM ON DATABASE TO USER {username};  

* Enable the \`Federation\` function on the target database. The sample commands are listed below:

\`\`\`bash
db2 update dbm cfg using federated yes
db2 force applications all
db2stop
db2start

\`\`\`
  
After the Target database is created, run the OnData Encryption Tool to extract a subset of data records from the Source database based on the subsetting 
configuration at the OnData Portal, process the extracted data, and store the processed data into the Target database.  
  
## 3. Apply Subsetting When Using OnData Encryption Tool
To apply the subsetting configuration with the OnData Encryption Tool, you just need to check the \`Subsetting Enabled\` option in \`Step 1.7 Encryption Settings\` 
of the instructions in [**Use OnData Encryption Tool**](reference_doc#securedb/ondata_encryption/use_ondata_encryption_tool).  
  
![Encryption Settings](${ img1 })  
  
### 3.1 Subsetting for Microsoft SQL Server Database
For a Microsoft SQL Server database, you just need to select the Target database created in \`Step 2.1 Microsoft SQL Server Database Prerequisites\` above and click 
on the \`Proceed\` button to start the data subsetting and encryption/obfuscation process. 
  
### 3.2 Subsetting for Oracle Database
For an Oracle database, you can proceed to the next step and connect to the Target Oracle database created in \`Step 2.2 Oracle Database Prerequisites\` above. After 
the Target database is connected, you will be prompted to select the database link created in \`Step 2.2 Oracle Database Prerequisites\` above. Click on the 
\`Proceed\` button to start the data subsetting and encryption/obfuscation process.
  
![Select Database Link](${ img2 })  
  
### 3.3 Subsetting for DB2 Database
For a DB2 database, you just need to select the Target database created in \`Step 2.3 DB2 Database Prerequisites\` above and click on the \`Proceed\` button to start 
the data subsetting and encryption/obfuscation process.  
  
**Note**: For DB2 databases, any tables with columns of user-defined types cannot be migrated. The following message will be displayed in the log file:  

\`\`\`bash
Table "SCHEMA_NAME"."TABLE_NAME" has columns of user-defined types and is ignored.

\`\`\`

**Note**: For DB2 databases, if a table is referenced by a Materialized Query Table (MQT) and has a primary key defined as \`GENERATED ALWAYS AS IDENTITY\`, the value in the 
identity column may not be the same as the source table after the migration process.  

**Note**: For all types of databases, if the subsetting process fails, there is no option to resume the process. You will have to resolve the root cause for the failure, clean up the Target 
database, and re-run the OnData Encryption Tool.  
  


  `
  
export default content;
